import React, { useCallback } from 'react'

import actions from 'actions'
import { useDispatch } from 'react-redux'

import { AddRecipeButton as AddRecipeButtons } from '@library/recipe-customisations'

import { useIsRecipeInStock } from 'hooks/menu-recipes/useIsRecipeInStock'
import type { RecipeCoreId, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { menuRecipeDetailVisibilityChange } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { formatSurcharge } from 'routes/Menu/utils/formatSurcharge'

import css from './AddRecipeButton.css'

type AddRecipeButtonProps = {
  id: RecipeUUID
  coreId: RecipeCoreId
  position?: string
  surcharge?: number
  surchargeLabel?: string
  isNegativeSurcharge?: boolean
}

export const AddRecipeButton = ({
  id,
  coreId,
  position = '',
  surcharge,
  surchargeLabel,
  isNegativeSurcharge,
}: AddRecipeButtonProps) => {
  const dispatch = useDispatch()
  const isOutOfStock = !useIsRecipeInStock(id)
  const { reachedLimit, getQuantitiesForRecipeId, canAddRecipes, addRecipe, removeRecipe } =
    useBasket()
  const qty = getQuantitiesForRecipeId(coreId)

  // TODO: Clean up this logic once fully migrated to the menu experience API
  const resolvedSurchargeLabel =
    surchargeLabel ?? (surcharge ? `${formatSurcharge(surcharge)} per portion` : '')
  const resolvedNegativeSurcharge = isNegativeSurcharge ?? Boolean(surcharge && surcharge < 0)

  const handleAdd = useCallback(
    (isFirstInBatchOfSameRecipes: boolean) => {
      if (!isOutOfStock && canAddRecipes) {
        addRecipe(coreId, 'view_recipe_detail_screen', { position })

        if (isFirstInBatchOfSameRecipes) {
          dispatch(menuRecipeDetailVisibilityChange())
        }
      } else {
        dispatch(menuRecipeDetailVisibilityChange())
        setTimeout(() => {
          dispatch(actions.menuBrowseCTAVisibilityChange(true))
        }, 500)
      }
    },
    [isOutOfStock, canAddRecipes, addRecipe, coreId, position, dispatch],
  )

  const handleRemove = useCallback(() => {
    removeRecipe(coreId, 'detail', position)
  }, [coreId, position, removeRecipe])

  return (
    <div className={css.addRecipeWrapper}>
      <AddRecipeButtons
        canAddRecipes={canAddRecipes}
        isNegativeSurcharge={resolvedNegativeSurcharge}
        isOutOfStock={isOutOfStock}
        onClickAdd={handleAdd}
        onClickRemove={handleRemove}
        quantity={qty}
        reachedLimit={reachedLimit}
        surchargeLabel={resolvedSurchargeLabel}
      />
    </div>
  )
}
